import { Edit } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';

import { AxleIconButton } from '../../../components/buttons/AxleIconButton';
import { FloatingActionButton } from '../../../components/buttons/FloatingActionButton';
import { QualificationChips } from '../../../components/displayFields/chips';
import {
    OrgDropdown,
    filterRowsBySelectedOrg,
    useOrgDropdownState,
} from '../../../components/formFields/OrgDropdown';
import { AxlePageHeader } from '../../../components/layout/AxlePageHeader';
import { AxleTable } from '../../../components/tables/AxleTable';
import { If } from '../../../components/utils/conditionalComponents';
import { useAppConfig } from '../../../fetching/api/appConfig';
import {
    DemandConnection,
    useDemandConnections,
} from '../../../fetching/api/supplyAndDemandConnections';
import { useBooleanState, useStateWithDerivedBoolean } from '../../../utils/booleanState';
import { CreateConnectionDialog } from '../../SupplyPage/SupplyConnections/CreateConnectionDialog';
import { EditDemandConnectionDialog } from './EditDemandConnectionDialog';

export const useShowDemandConnections = (): boolean => {
    const appConfig = useAppConfig();
    const { allDemandConnections } = useDemandConnections();

    return (
        appConfig.upstream_handshake.page &&
        // Hide demand connections if there are no demand connections, nor can the user create one.
        (allDemandConnections.length > 0 || appConfig.upstream_handshake.create)
    );
};

export const DemandConnections = () => {
    const appConfig = useAppConfig();
    const showDemandConnections = useShowDemandConnections();
    const { allDemandConnections } = useDemandConnections();

    const allToOrgs = allDemandConnections.map(
        (demandConnection) => demandConnection.handshake.to_org,
    );
    const orgDropdownState = useOrgDropdownState({
        showOptionToSelectAll: true,
        defaultToAll: false,
    });
    const [demandConnections, hideOrgColumn] = filterRowsBySelectedOrg({
        rows: allDemandConnections,
        rowToOrg: (demandConnection) => demandConnection.handshake.to_org,
        orgDropdownStateValue: orgDropdownState.value,
    });

    const [isCreateDialogOpen, openCreateDialog, closeCreateDialog] = useBooleanState(false);
    const [demandConnectionToEdit, setDemandConnectionToEdit, isEditDialogOpen, closeEditDialog] =
        useStateWithDerivedBoolean<DemandConnection>(null);

    return (
        <If condition={showDemandConnections}>
            <AxlePageHeader title={'Clients'}>
                <OrgDropdown inputState={orgDropdownState} options={allToOrgs} />
                <If condition={appConfig.upstream_handshake.create}>
                    <FloatingActionButton
                        icon={<AddIcon />}
                        text={'Create Connection'}
                        onClick={openCreateDialog}
                    />
                </If>
            </AxlePageHeader>
            <CreateConnectionDialog isOpen={isCreateDialogOpen} close={closeCreateDialog} />
            <EditDemandConnectionDialog
                isOpen={isEditDialogOpen}
                close={closeEditDialog}
                demandConnection={demandConnectionToEdit}
            />
            <AxleTable
                data={demandConnections}
                elevation={1}
                columns={[
                    {
                        label: 'Organization',
                        field: 'handshake.to_org.name',
                        hidden: hideOrgColumn,
                    },
                    {
                        label: 'Demand Connection',
                        field: 'handshake.from_org.name',
                    },
                    {
                        label: 'Qualifications Required',
                        rowToComponent: (demandConnection: DemandConnection) => (
                            <QualificationChips
                                qualifications={demandConnection.qualification_requirements}
                            />
                        ),
                    },
                    {
                        rowToComponent: (demandConnection: DemandConnection) => (
                            <AxleIconButton
                                icon={<Edit />}
                                tooltip={'Edit Demand Connection'}
                                onClick={() => setDemandConnectionToEdit(demandConnection)}
                            />
                        ),
                    },
                ]}
            />
        </If>
    );
};
